import React, { Fragment } from 'react';

export function getCarmaxOfferFaqContent() {
  return [
    {
      question: 'Do I need to buy a car from a dealer to sell one?',
      answer:
        'Unless otherwise specified, you can sell directly to one of our partners without purchasing another vehicle.',
    },
    {
      question: 'What do I need to sell my car?',
      answer:
        'Requirements vary by state and dealer, but generally you&apos;ll need: your car&apos;s title or payoff information (all titleholders present); valid and current registration; valid state-issued photo ID for all title holders; all keys and remotes. Check with your specific offer partner for additional requirements.',
    },
    {
      question: 'Will my offer price change when the dealer sees my car?',
      answer:
        'The offer provider you choose to sell with will verify that all of the information provided online is accurate. As long as that is the case, you will receive the exact amount of your online offer.',
    },
    {
      question: 'How do I get paid for my car?',
      answer:
        'In most cases, you will leave with payment in hand the same day you sell your car. Payment methods may vary by dealer or offer partner &mdash; common methods include bank drafts, checks, or electronic transfers. A bank draft must be deposited and cannot be cashed. Check with your bank about any hold policy on deposits.',
    },
  ];
}

export const ESTIMATED_OFFER_ENTRY = {
  question: 'What is the difference between an estimated offer and an online offer?',
  answer: [
    'If you see a single offer price, then you have received a firm online offer. If your answers are accurate, you should receive this amount when you sell your car.',
    'If you see a range of prices, this means you received an estimated offer. The dealer or offer partner needs additional information to accurately appraise your vehicle. They will need to appraise your car in person. In some cases, the actual offer may fall outside the range shown.',
  ],
};

export function getCarmaxEstimatedOfferFaqContent() {
  return [
    {
      question: 'Do I need to buy a car from a dealer to sell one?',
      answer:
        'Unless otherwise specified, you can sell directly to one of our partners without purchasing another vehicle.',
    },
    {
      question: 'What do I need to sell my car?',
      answer:
        'Requirements vary by state and dealer, but generally you&apos;ll need: your car&apos;s title or payoff information (all titleholders present); valid and current registration; valid state-issued photo ID for all title holders; all keys and remotes. Check with your specific offer provider for additional requirements.',
    },
    {
      question: 'Will my offer price change when the dealer sees my car?',
      answer: [
        'The offer provider you choose to sell with will verify that all of the information provided online is accurate. As long as that is the case, you will receive the exact amount of your online offer.',
      ],
    },
    ESTIMATED_OFFER_ENTRY,
    {
      question: 'How do I get paid for my car?',
      answer:
        'In most cases, you will leave with payment in hand the same day you sell your car. Payment methods may vary by dealer or offer partner &mdash; common methods include bank drafts, checks, or electronic transfers. A bank draft must be deposited and cannot be cashed. Check with your bank about any hold policy on deposits.',
    },
  ];
}

export const WHY_GET_OFFER = {
  question: 'Why should I get an offer with my appraisal?',
  answer:
    "An Edmunds appraisal gives you a True Market Value&reg; which is Edmunds' estimate of the average price dealers are currently paying for this vehicle in your area. This is a useful benchmark, but not a guarantee that someone will buy your car at that price. A firm offer, on the other hand, is a real commitment to purchase your vehicle, typically good for seven days at a participating dealership. If you're selling privately, you can use this offer as a baseline for your asking price. Or, if you're comfortable with the offer, you can accept it and redeem it at a participating location.",
};

export const WHAT_IS_RELATIONSHIP_BETWEEN_EDMUNDS_AND_CARMAX = {
  question: 'What is the relationship between Edmunds and CarMax?',
  answer: [
    'Since June 2021, Edmunds has been a wholly owned subsidiary of CarMax, Inc.',
    'To learn more about the relationship between Edmunds and CarMax, see our <a href="/about/visitor-agreement.html">Visitor Agreement</a>.',
  ],
};

function getCommonPartnerFaqContent() {
  return [
    {
      question: 'Why do you need my VIN or license plate?',
      answer:
        'Your VIN or license plate is used to identify your vehicle and determine if you are eligible to receive one or more instant offers with your appraisal report. Not all vehicles qualify for an online offer based on each partner&apos;s eligibility criteria, which may include a vehicle history check.',
    },
    WHY_GET_OFFER,
    WHAT_IS_RELATIONSHIP_BETWEEN_EDMUNDS_AND_CARMAX,
  ];
}

export const IS_CARMAX_OFFER_REAL = {
  question: 'Is the offer a real offer?',
  answer:
    'Yes! As long as the information you provided online is accurate and you visit the participating dealership before the offer expires, you should receive the offer amount shown.',
};

export const getHowCarmaxDetermineOffer = ({ appendSlot = null } = {}) => ({
  question: 'How is my offer determined?',
  answer: (
    <Fragment>
      <span>
        Our partners analyze your car&apos;s specific year, make, model, mileage, and features. They consider its
        condition, history, and use based on information you provide, as well as their own assessments and internal and
        third-party history information. They especially look out for major issues, like frame or flood damage or
        odometer rollbacks. Market conditions and sales performance are also considered. When you redeem your offer in
        person, the offer partner may need to test drive your car or perform additional checks on its mechanical
        condition. The long-term maintenance and care of your car is more important than cleanliness when verifying your
        offer in person.
      </span>{' '}
      {appendSlot}
    </Fragment>
  ),
  omitContentFragment: true,
});

export const DOES_CARMAX_BUY_LEASED_CARS = {
  question: 'Can I sell a leased car?',
  answer:
    'In most cases, you can sell your leased car in almost the same way as any other financed car. The offer partner will appraise it, contact the leasing company for a payoff quote, and process any equity. Note: not all partners can purchase leased vehicles, and some leasing companies may restrict third-party buyouts. For CarMax-specific details, click <a href ="https://www.carmax.com/sell-my-car#faq-lease" target="_blank">here</a>.',
};

export const IF_I_OWE_MORE = {
  question: 'What if I owe more on my car than the amount of my offer?',
  answer: `If your payoff amount is more than the offer on your car, the difference is called "negative equity." In some cases, it can be included in financing for your next car. Otherwise, the dealer will calculate the difference and you can pay directly. Accepted payment methods vary by dealer. Offers on Edmunds are typically good for seven days, giving you time to arrange funds.`,
};

export const CAN_I_NEGOTIATE = {
  question: 'Can I negotiate the online offer?',
  answer: 'No. All offers received on Edmunds.com are firm.',
};

export const WILL_CARMAX_PICK_UP = {
  question: 'Will the dealer pick up my vehicle from my home?',
  answer:
    'Some of our offer partners may offer vehicle pickup service in select markets. Check with the dealer listed in your offer to confirm availability. In most cases, you can also redeem your offer by visiting a participating location.',
};

export const DO_I_NEED_AN_APPOINTMENT = {
  question: 'Do I need an appointment to sell my car?',
  answer:
    "No, you don't need an appointment to sell your car. However, an appointment will help get you in and out in as little time as possible.",
};

export const WHAT_IS_EDMUNDS_TMV = {
  question: 'What is Edmunds True Market Value®?',
  answer: [
    'Edmunds True Market Value&reg; (TMV&reg;), also known as Edmunds Suggested Price, is a pricing system that helps you determine the average transaction price &mdash; or what others are paying &mdash; for new or used vehicles in your area so that you can begin your negotiations with a fair price in mind. For customers appraising their vehicles for sale, Edmunds TMV&reg; helps you understand how much you might expect to receive when you sell.',
    'We determine those prices based on a variety of factors, including actual transaction data from dealers. CarMax is one of the dealers from which Edmunds obtains transaction data.',
    'Since June 2021, Edmunds has been a wholly owned subsidiary of CarMax, Inc. However, other than the incorporation of CarMax&apos;s transaction data by Edmunds into TMV&reg; calculations, CarMax does not participate in or otherwise influence the manner in which Edmunds computes those values.',
  ],
};

export function getAppraiserFaqContent(commonFaqContent = []) {
  return [
    ...getCommonPartnerFaqContent(),
    ...getCarmaxEstimatedOfferFaqContent(),
    ...commonFaqContent,
    IS_CARMAX_OFFER_REAL,
    getHowCarmaxDetermineOffer(),
    DOES_CARMAX_BUY_LEASED_CARS,
    IF_I_OWE_MORE,
    CAN_I_NEGOTIATE,
    WILL_CARMAX_PICK_UP,
    DO_I_NEED_AN_APPOINTMENT,
  ];
}
